import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AddStudent from './AddStudent'; // a new student without a free class
import { useSearchParams, useNavigate } from 'react-router-dom';
import { parsePhoneNumber } from 'react-phone-number-input';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCakeCandles,
  faChalkboardUser,
  faUserGraduate,
  faMoneyBillWave,
  faCalendarPlus,
  faSignal,
  faClockRotateLeft,
  faCalendarDays,
  faMagnifyingGlass,
  faPlus,
  faArrowUpRightFromSquare,
  faArrowUp,
  faArrowDown,
  faSort,
  faTriangleExclamation,
  faCircleCheck,
  faXmark,
  faBell,
  faReceipt,
  faCheck,
  faUsers,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { countryFromTz } from './Studentzone';
import { DEMO_COURSES } from '../data/courses';
import './Users.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

/* ---------- Tabs (same sections as before) ---------- */
const TABS = [
  { key: 'teachers', label: 'Teachers', icon: faChalkboardUser, subs: [['active', 'Active'], ['inactive', 'Inactive']] },
  { key: 'students', label: 'Students', icon: faUserGraduate, subs: [['paid', 'Paid'], ['demo', 'Demo']] },
  { key: 'studentsunpaid', label: 'Unpaid', icon: faMoneyBillWave },
  { key: 'birthdays', label: 'Birthdays', icon: faCakeCandles },
  { key: 'demorequest', label: 'Demo requests', icon: faCalendarPlus },
  { key: 'studentsupdate', label: 'Students update', icon: faClockRotateLeft, subs: [['update', 'Needs update'], ['paused', 'Paused'], ['pending', 'Pending (not paid)'], ['notscheduled', 'Not scheduled']] },
  { key: 'demoavailability', label: 'Demo availability', icon: faCalendarDays },
];

/* Same endpoints as before */
const urlFor = (tab, sub) => {
  switch (tab) {
    case 'teachers':
      return `${API}/api/admin/teachers/${sub}`;
    case 'students':
      return `${API}/api/admin/students/st/${sub}`;
    case 'demorequest':
      return `${API}/api/admin/students/demorequest`;
    case 'onlineusers':
      return `${API}/api/admin/students/online`;
    case 'studentsupdate':
      return `${API}/api/admin/students/studentsupdate?view=${['paused', 'pending', 'notscheduled'].includes(sub) ? sub : 'update'}`;
    case 'studentsunpaid':
      return `${API}/api/admin/students/unpaid`;
    case 'demoavailability':
      return `${API}/api/admin/teachers/demoavailability/all`;
    default:
      return '';
  }
};

const NOUN = {
  teachers: ['teacher', 'teachers'],
  students: ['student', 'students'],
  studentsunpaid: ['student with unpaid sessions', 'students with unpaid sessions'],
  demorequest: ['demo request', 'demo requests'],
  onlineusers: ['user online', 'users online'],
  studentsupdate: ['student', 'students'],
  birthdays: ['birthday', 'birthdays'],
};

/* ---------- Birthdays tab: paid students with a birthday, next one first ---------- */
const birthParts = (value) => {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? { y: +m[1], m: +m[2], d: +m[3] } : null;
};
const withNextBirthday = (u) => {
  const b = birthParts(u.Birthday);
  if (!b) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const on = (y) => {
    // 29 Feb in a normal year -> 28 Feb (the day the email goes out)
    const leap = (yy) => (yy % 4 === 0 && yy % 100 !== 0) || yy % 400 === 0;
    const d = b.m === 2 && b.d === 29 && !leap(y) ? 28 : b.d;
    return new Date(y, b.m - 1, d);
  };
  let next = on(today.getFullYear());
  if (next < today) next = on(today.getFullYear() + 1);
  let days = Math.round((next - today) / 86400000);
  if (u.DueDaysAgo != null && u.DueDaysAgo > 0) {
    // birthday a few days ago, email still to send: keep it at the top
    next = new Date(today.getFullYear(), today.getMonth(), today.getDate() - u.DueDaysAgo);
    days = -u.DueDaysAgo;
  }
  return { ...u, NextBirthday: next, DaysToBirthday: days, TurnsAge: next.getFullYear() - b.y };
};
// birthday today (or up to 3 days ago), email not sent yet, parent email saved
const isBirthdayDue = (u) => u.DueDaysAgo != null && !u.EmailSent && !!String(u.ParentsEmail || '').trim();
const inDays = (n) => (n < 0 ? (n === -1 ? 'Yesterday' : `${-n} days ago`) : n === 0 ? 'Today 🎂' : n === 1 ? 'Tomorrow' : n < 7 ? `In ${n} days` : n < 60 ? `In ${Math.round(n / 7)} weeks` : `In ${Math.round(n / 30)} months`);

const listOf = (res) => {
  const d = res?.data?.data || res?.data;
  return Array.isArray(d) ? d : [];
};

/* ---------- Helpers ---------- */
const isLittle = (u) => Number(u.IdGradesPlans) === 1; // otherwise Junior
const fullName = (u) => [u.FirstName, u.LastName].filter(Boolean).join(' ') || 'No name';
const digits = (p) => String(p || '').replace(/\D/g, '');
const waUrl = (phone, text = '') =>
  `https://api.whatsapp.com/send/?phone=${digits(phone)}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

/* Demo requests: country flag + place.
   Flag: from the time zone, like the Students and Classes pages
   (if there is none: the IP country, then the phone's country code).
   Place: from the IP address when the form was sent (new requests only). */
const flagOf = (code) => (/^[A-Z]{2}$/i.test(code || '') ? String(code).toLowerCase() : '');
const phoneCountry = (phone) => {
  try {
    const p = String(phone || '').trim();
    return parsePhoneNumber(p.startsWith('+') ? p : `+${digits(p)}`)?.country || '';
  } catch {
    return '';
  }
};
const placeOf = (u) => [u.City, u.Region && u.Region !== u.City ? u.Region : null, u.Country].filter(Boolean).join(', ');

const calculateAge = (birthday) => {
  if (!birthday) return null;
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  return age;
};

const ageInfo = (u) => {
  const hasBirthday = !!u.Birthday;
  const calc = hasBirthday ? calculateAge(u.Birthday) : null;
  return { hasBirthday, calc, mismatch: hasBirthday && calc !== null && calc !== u.Age };
};

/* "UTC±HH:MM" from hours (same as before) */
const formatOffset = (hours) => {
  if (hours === null || hours === undefined) return '';
  const num = Number(hours);
  if (Number.isNaN(num)) return '';
  const sign = num >= 0 ? '+' : '-';
  const abs = Math.abs(num);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const ago = (date) => {
  const diff = Date.now() - date.getTime();
  if (diff < 0) return '';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return mo < 12 ? `${mo} mo ago` : `${Math.floor(mo / 12)}y ago`;
};

const When = ({ value, never = 'Never' }) => {
  if (!value) return <span className="rmusr-dim">{never}</span>;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return <span className="rmusr-dim">—</span>;
  return (
    <span className="rmusr-when" title={d.toLocaleString()}>
      <span className="rmusr-when-date">
        {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined })}
        {', '}
        {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
      <small>{ago(d)}</small>
    </span>
  );
};

const Online = ({ on }) => <span className={`rmusr-pill ${on ? 'is-good' : 'is-off'}`}><i />{on ? 'Online' : 'Offline'}</span>;

const money2 = (amount) => {
  if (amount === null || amount === undefined) return '0.00';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Number.isNaN(n) ? '0.00' : n.toFixed(2);
};
const sessionsOf = (s) => {
  if (s === null || s === undefined) return 0;
  const n = typeof s === 'string' ? parseInt(s, 10) : s;
  return Number.isNaN(n) ? 0 : n;
};

/* ---------- Payment WhatsApp messages (same format as Student profile > Payments) ----------
   WhatsApp: *bold*, _italic_ */
const usd = (v) => {
  const n = Number(money2(v));
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};
const paymentMessage = (kind, s) => {
  const name = fullName(s) === 'No name' ? '' : fullName(s);
  const n = sessionsOf(s.UnpaidSessions);
  const block = [
    `*Student:* ${name}`,
    `*Amount:* ${usd(s.UnpaidAmount)}`,
    n ? `*Number of Sessions:* ${n} ${n === 1 ? 'session' : 'sessions'}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  const transfer =
    '_You can transfer the amount via Western Union, OMT, WHISH, or BOB using the following details:_\n\n' +
    '*Recipient:* Nadim Nasrallah\n' +
    '*Recipient Number:* 03315951';
  if (kind === 'details') return `*Registration Update:*\n${block}\n\n${transfer}`;
  if (kind === 'gentle') return `Hello, this is a gentle reminder about the payment registration 😊\n\n${block}\n\n${transfer}`;
  return (
    '*URGENT: Payment registration*\n\n' +
    'Hello, this is an urgent reminder about the pending payment registration. ' +
    'Please complete it as soon as possible to avoid any interruption in the sessions.\n\n' +
    `${block}\n\n${transfer}\n\n` +
    '_If you have already paid, please ignore this message._'
  );
};

/* ---------- Demo availability: group slots by day (same logic as before) ---------- */
const groupByDate = (data) => {
  const map = new Map();
  [...(data || [])]
    .sort((a, b) => new Date(a.AdjustedDateTime) - new Date(b.AdjustedDateTime))
    .forEach((slot) => {
      const dt = new Date(slot.AdjustedDateTime);
      const key = dt.toDateString();
      if (!map.has(key)) map.set(key, { date: key, fullDate: dt, slots: [] });
      map.get(key).slots.push({
        ...slot,
        time: dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        teacherName:
          slot.TeacherFirstName && slot.TeacherLastName
            ? `${slot.TeacherFirstName} ${slot.TeacherLastName}`
            : `${slot.FirstName || ''} ${slot.LastName || ''}`.trim(),
        teacherId: slot.TeacherId || slot.IdUser,
        isAvailable: slot.Check === 1,
        isTaken: slot.Taken === 1,
      });
    });
  return Array.from(map.values()).sort((a, b) => a.fullDate - b.fullDate);
};

const DateStrip = ({ dates, value, onChange }) => (
  <div className="rmusr-dates" role="radiogroup" aria-label="Date">
    {dates.map((d) => (
      <button key={d.date} type="button" role="radio" aria-checked={value === d.date} className={value === d.date ? 'is-on' : ''} onClick={() => onChange(d.date)}>
        <span>{d.fullDate.toLocaleDateString('en-US', { weekday: 'short' })}</span>
        <strong>{d.fullDate.getDate()}</strong>
        <span>{d.fullDate.toLocaleDateString('en-US', { month: 'short' })}</span>
        <em>
          {d.slots.length} slot{d.slots.length !== 1 ? 's' : ''}
        </em>
      </button>
    ))}
  </div>
);

const EMPTY_TEACHER = { firstName: '', lastName: '', email: '', mobileNumber: '', username: '', password: '', confirmPassword: '', birthday: '' };

/* =========================================================
   Component
   ========================================================= */
const Users = ({ userData }) => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tabDef = TABS.find((t) => t.key === params.get('tab')) || TABS[0];
  const tab = tabDef.key;
  const sub = tabDef.subs ? (tabDef.subs.some(([k]) => k === params.get('sub')) ? params.get('sub') : tabDef.subs[0][0]) : '';
  const viewKey = `${tab}/${sub}`;

  const [data, setData] = useState({ key: '', rows: [] }); // rows belong to data.key
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState('all'); // Students tab: all / little / junior
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [toast, setToast] = useState('');
  const [savingCourse, setSavingCourse] = useState(null); // demo request Id whose course is being saved
  const [fixingAge, setFixingAge] = useState(null); // IdUser being fixed
  const [bdayDue, setBdayDue] = useState(0); // birthday emails waiting to be sent (badge on the tab)
  const [sendingBday, setSendingBday] = useState(null); // IdUser being emailed

  const [dayKey, setDayKey] = useState(null); // demo availability tab
  const [approve, setApprove] = useState(null); // { request, loading, teachers, selected, submitting, error }
  const [avail, setAvail] = useState(null); // { teacher, loading, dates, day, error }
  const [addOpen, setAddOpen] = useState(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false); // Add student (no free class)
  const [newTeacher, setNewTeacher] = useState(EMPTY_TEACHER);
  const [addErrors, setAddErrors] = useState({});
  const [adding, setAdding] = useState(false);

  const adminId = userData?.id;

  const go = (key, nextSub) => {
    const next = new URLSearchParams(params);
    if (key === 'teachers') next.delete('tab');
    else next.set('tab', key);
    if (nextSub) next.set('sub', nextSub);
    else next.delete('sub');
    setParams(next, { replace: true });
    setSearch('');
    setSortConfig({ key: null, direction: 'asc' });
  };

  const setSub = (s) => {
    const next = new URLSearchParams(params);
    if (s === tabDef.subs[0][0]) next.delete('sub');
    else next.set('sub', s);
    setParams(next, { replace: true });
    setSortConfig({ key: null, direction: 'asc' });
  };

  // ---------- Load (same endpoints). "Online" refreshes quietly every 30s ----------
  useEffect(() => {
    if (!adminId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        if (tab === 'birthdays') {
          // paid students with a birthday: birthdays to send first, then the next ones
          const res = await axios.get(`${API}/api/admin/students/birthdays`, auth());
          if (cancelled) return;
          const rows = listOf(res)
            .map(withNextBirthday)
            .filter(Boolean)
            .sort((a, b) => a.DaysToBirthday - b.DaysToBirthday || a.TurnsAge - b.TurnsAge);
          setData({ key: viewKey, rows });
          setBdayDue(rows.filter(isBirthdayDue).length);
          setError('');
          return;
        }
        const res = await axios.get(urlFor(tab, sub), auth());
        if (cancelled) return;
        setData({ key: viewKey, rows: listOf(res) });
        setError('');
      } catch (err) {
        console.error('Fetch error:', err);
        if (!cancelled) setError(err.response?.data?.error || 'We couldn’t load this list.');
      }
    };
    setError('');
    load();
    const id = tab === 'onlineusers' ? setInterval(load, 30000) : null;
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [adminId, tab, sub, viewKey, reload]);

  // badge on the Birthdays tab: birthday emails waiting to be sent (checked when the page opens)
  useEffect(() => {
    if (!adminId) return;
    axios
      .get(`${API}/api/admin/students/birthdays`, auth())
      .then((res) => setBdayDue(listOf(res).filter(isBirthdayDue).length))
      .catch(() => {});
  }, [adminId]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const ready = data.key === viewKey;
  const rows = useMemo(() => (ready ? data.rows : []), [ready, data.rows]);

  // ---------- Search (same fields as before: name, parent email, id) ----------
  // Little / Junior (Students tab only, once the server sends IdGradesPlans)
  const hasLevel = tab === 'students' && rows.some((u) => u.IdGradesPlans !== undefined);
  const levelRows = useMemo(
    () => (!hasLevel || level === 'all' ? rows : rows.filter((u) => (level === 'little' ? isLittle(u) : !isLittle(u)))),
    [rows, hasLevel, level]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return levelRows;
    return levelRows.filter(
      ({ FirstName, LastName, IdUser, ParentsEmail }) =>
        [FirstName, LastName, ParentsEmail, `${FirstName || ''} ${LastName || ''}`].some((f) => f?.toLowerCase().includes(term)) ||
        IdUser?.toString().includes(term)
    );
  }, [levelRows, search]);

  // ---------- Sorting (same rules as before) ----------
  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    const { key, direction } = sortConfig;
    return [...filtered].sort((a, b) => {
      let aValue = a[key] == null ? '' : a[key];
      let bValue = b[key] == null ? '' : b[key];

      if (key === 'LastSeen' || key === 'Lastsignin' || key === 'Birthday') {
        aValue = a[key] ? new Date(a[key]).getTime() : 0;
        bValue = b[key] ? new Date(b[key]).getTime() : 0;
      }
      if (key === 'Name') {
        aValue = `${a.FirstName || ''} ${a.LastName || ''}`.toLowerCase();
        bValue = `${b.FirstName || ''} ${b.LastName || ''}`.toLowerCase();
      }
      if (key === 'UnpaidAmount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      if (key === 'UnpaidSessions') {
        aValue = parseInt(aValue, 10) || 0;
        bValue = parseInt(bValue, 10) || 0;
      }
      if (key === 'TimezoneName') {
        aValue = (a.TimezoneName || 'zzzz').toLowerCase();
        bValue = (b.TimezoneName || 'zzzz').toLowerCase();
      }
      if (key === 'AgeMismatch') {
        aValue = ageInfo(a).mismatch ? 1 : 0;
        bValue = ageInfo(b).mismatch ? 1 : 0;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return direction === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
    });
  }, [filtered, sortConfig]);

  // asc -> desc -> off (same as before)
  const handleSort = (key) => {
    if (sortConfig.key === key && sortConfig.direction === 'asc') setSortConfig({ key, direction: 'desc' });
    else if (sortConfig.key === key) setSortConfig({ key: null, direction: 'asc' });
    else setSortConfig({ key, direction: 'asc' });
  };

  // ---------- Demo availability tab ----------
  const allDates = useMemo(() => (tab === 'demoavailability' ? groupByDate(rows) : []), [tab, rows]);
  const currentDay = allDates.find((d) => d.date === dayKey) || allDates[0] || null;

  // ---------- Approve demo request (same calls) ----------
  const openApprove = async (request) => {
    setApprove({ request, loading: true, teachers: [], selected: '', submitting: false, error: '' });
    try {
      const res = await axios.get(`${API}/api/admin/students/demoapprove/${request.ClassDateTime}`, auth());
      const teachers = Array.isArray(res.data) ? res.data : listOf(res);
      setApprove((a) => (a && a.request.Id === request.Id ? { ...a, loading: false, teachers } : a));
    } catch (err) {
      setApprove((a) =>
        a && a.request.Id === request.Id ? { ...a, loading: false, error: err.response?.data?.error || 'We couldn’t load the available teachers.' } : a
      );
    }
  };

  const submitApprove = async () => {
    if (!approve?.selected) return;
    const { request, selected } = approve;
    setApprove((a) => ({ ...a, submitting: true, error: '' }));
    try {
      const done = await axios.post(`${API}/api/admin/students/demoapproved`, { IdUser: selected, Id: request.Id }, auth());
      setApprove(null);
      // go straight to the new demo student's profile
      if (done.data?.studentId) {
        navigate(`/Admin/studentprofile/${done.data.studentId}`);
        return;
      }
      const res = await axios.get(`${API}/api/admin/students/demorequest`, auth());
      setData({ key: 'demorequest/', rows: listOf(res) });
      setToast(`Demo for ${fullName(request)} approved.`);
    } catch (err) {
      setApprove((a) => (a ? { ...a, submitting: false, error: err.response?.data?.error || err.message || 'Approve failed.' } : a));
    }
  };

  // ---------- Change the course of a demo request (parent picked the wrong one) ----------
  const changeCourse = async (request, course) => {
    setSavingCourse(request.Id);
    try {
      await axios.put(`${API}/api/admin/students/demorequest/${request.Id}/course`, { course }, auth());
      setData((d) => ({
        ...d,
        rows: d.rows.map((row) => (row.Id === request.Id ? { ...row, InterestedCourse: course || null } : row)),
      }));
      setToast(`Course for ${fullName(request)} changed to ${course || 'Not sure'}.`);
    } catch (err) {
      setToast(err.response?.data?.error || 'We couldn’t change the course. Please try again.');
    } finally {
      setSavingCourse(null);
    }
  };

  // ---------- One teacher's demo availability (same call) ----------
  const openAvailability = async (teacher) => {
    setAvail({ teacher, loading: true, dates: [], day: null, error: '', demosOff: false });
    try {
      const res = await axios.get(`${API}/api/admin/teachers/demoavailability/teacher/${teacher.IdUser}`, auth());
      const demosOff = res.data?.demosOff === true;
      const dates = demosOff ? [] : groupByDate(listOf(res));
      setAvail((a) => (a && a.teacher.IdUser === teacher.IdUser ? { ...a, loading: false, demosOff, dates, day: dates[0]?.date || null } : a));
    } catch (err) {
      console.error('Error fetching teacher availability:', err);
      setAvail((a) =>
        a && a.teacher.IdUser === teacher.IdUser ? { ...a, loading: false, error: err.response?.data?.error || 'We couldn’t load this availability.' } : a
      );
    }
  };

  // ---------- Add teacher (same checks + same call) ----------
  const openAdd = () => {
    setNewTeacher(EMPTY_TEACHER);
    setAddErrors({});
    setAddOpen(true);
  };

  const onNewTeacher = (e) => {
    const { name, value } = e.target;
    setNewTeacher((p) => ({ ...p, [name]: value }));
    if (addErrors[name]) setAddErrors((p) => ({ ...p, [name]: null }));
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!newTeacher.firstName.trim()) errors.firstName = 'First name is required';
    if (!newTeacher.lastName.trim()) errors.lastName = 'Last name is required';
    if (!newTeacher.email.trim()) errors.email = 'Email is required';
    if (!newTeacher.username.trim()) errors.username = 'Username is required';
    if (!newTeacher.password) errors.password = 'Password is required';
    else if (newTeacher.password.length < 6) errors.password = 'At least 6 characters';
    if (newTeacher.password !== newTeacher.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (Object.keys(errors).length) {
      setAddErrors(errors);
      return;
    }

    setAdding(true);
    setAddErrors({});
    try {
      await axios.post(
        `${API}/api/admin/teacherProfile/create`,
        {
          firstName: newTeacher.firstName,
          lastName: newTeacher.lastName,
          email: newTeacher.email,
          mobileNumber: newTeacher.mobileNumber,
          username: newTeacher.username,
          password: newTeacher.password,
          birthday: newTeacher.birthday || null,
        },
        auth()
      );
      setAddOpen(false);
      setToast(`${newTeacher.firstName} ${newTeacher.lastName} was added.`);
      // reload the real list (new teachers are active)
      if (tab === 'teachers' && sub === 'active') setReload((n) => n + 1);
      else go('teachers', null);
    } catch (err) {
      setAddErrors({ submit: err.response?.data?.error || err.message || 'Failed to create teacher' });
    } finally {
      setAdding(false);
    }
  };

  // Escape closes the open window
  const closeAll = useCallback(() => {
    setApprove((a) => (a?.submitting ? a : null));
    setAvail(null);
    setAddOpen((o) => (adding ? o : false));
  }, [adding]);

  useEffect(() => {
    if (!approve && !avail && !addOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && closeAll();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [approve, avail, addOpen, closeAll]);

  /* ---------- Columns per tab ---------- */
  const nameCell = (u, type) => (
    <span className="rmusr-name">
      <a href={`/Admin/${type === 'teacher' ? 'teacherprofile' : 'studentprofile'}/${u.IdUser}`} target="_blank" rel="noopener noreferrer">
        {fullName(u)}
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </a>
    </span>
  );

  const phoneCell = (u) =>
    u.MobileNumber ? (
      <a className="rmusr-wa" href={waUrl(u.MobileNumber)} target="_blank" rel="noopener noreferrer">
        <FontAwesomeIcon icon={faWhatsapp} /> {u.MobileNumber}
      </a>
    ) : (
      <span className="rmusr-dim">No number</span>
    );

  const COLUMNS = {
    teachers: [
      { label: 'Teacher', sort: 'Name', main: true, cell: (u) => nameCell(u, 'teacher') },
      { label: 'Last sign-in', m: 'hide', sort: 'Lastsignin', cell: (u) => <When value={u.Lastsignin} never="Never signed in" /> },
      { label: 'Last seen', m: 'sub', sort: 'LastSeen', cell: (u) => <When value={u.LastSeen} /> },
      {
        label: 'Timezone', m: 'sub',
        sort: 'TimezoneName',
        cell: (u) =>
          u.TimezoneName ? (
            <span className="rmusr-tz">
              {u.TimezoneName.replace(/_/g, ' ')}
              <small>{formatOffset(u.TimezoneOffset)}</small>
            </span>
          ) : (
            <span className="rmusr-pill is-warn">Not set</span>
          ),
      },
      {
        label: 'Status', m: 'sub',
        cell: (u) => <span className={`rmusr-pill ${u.IsActive === true ? 'is-good' : 'is-bad'}`}>{u.IsActive === true ? 'Active' : 'Inactive'}</span>,
      },
      { label: 'Online', m: 'side', cell: (u) => <Online on={u.IsOnline} /> },
      {
        label: '',
        action: true,
        cell: (u) => (
          <button type="button" className="rmusr-btn rmusr-btn--ghost rmusr-btn--sm" onClick={() => openAvailability(u)}>
            <FontAwesomeIcon icon={faCalendarDays} /> Availability
          </button>
        ),
      },
    ],
    students: [
      { label: 'Student', sort: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
      {
        label: 'Birthday', m: 'hide',
        sort: 'Birthday',
        cell: (u) => {
          const a = ageInfo(u);
          return a.hasBirthday ? (
            <span className={a.mismatch ? 'rmusr-flag' : ''}>{new Date(u.Birthday).toLocaleDateString()}</span>
          ) : (
            <span className="rmusr-dim">Not set</span>
          );
        },
      },
      {
        label: 'Age', m: 'sub',
        sort: 'AgeMismatch',
        cell: (u) => {
          const a = ageInfo(u);
          return (
            <span className={a.mismatch ? 'rmusr-flag' : ''}>
              {u.Age || 'N/A'}
              {a.mismatch && (
                <small>
                  <FontAwesomeIcon icon={faTriangleExclamation} /> should be {a.calc}
                </small>
              )}
              {a.mismatch && a.calc >= 3 && a.calc <= 99 && (
                <button
                  type="button"
                  className="rmusr-btn rmusr-btn--ghost rmusr-btn--sm rmusr-agefix"
                  onClick={() => fixAge(u)}
                  disabled={fixingAge === u.IdUser}
                  title="Set the age from the birthday (check the birthday first)"
                >
                  {fixingAge === u.IdUser ? 'Saving…' : `Fix to ${a.calc}`}
                </button>
              )}
            </span>
          );
        },
      },
      { label: 'Last sign-in', m: 'sub', sort: 'Lastsignin', cell: (u) => <When value={u.Lastsignin} never="Never signed in" /> },
    ],
    birthdays: [
      { label: 'Student', sort: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
      {
        label: 'Birthday', m: 'sub',
        cell: (u) => (
          <span className="rmusr-when">
            {u.NextBirthday.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long', ...(u.NextBirthday.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) })}
            <small>Born {new Date(u.Birthday).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</small>
          </span>
        ),
      },
      {
        label: 'When', m: 'side',
        cell: (u) => (
          <span className={`rmusr-pill ${u.DaysToBirthday === 0 ? 'is-good' : u.DaysToBirthday <= 7 ? 'is-warn' : 'is-off'}`}>
            {inDays(u.DaysToBirthday)}
          </span>
        ),
      },
      { label: 'Turns', m: 'sub', cell: (u) => <strong>{u.TurnsAge}</strong> },
      {
        label: 'Birthday email',
        action: true,
        cell: (u) => {
          if (u.DueDaysAgo == null) return <span className="rmusr-dim">On the day</span>;
          if (u.EmailSent) return <span className="rmusr-pill is-good">Sent ✓</span>;
          if (!String(u.ParentsEmail || '').trim()) return <span className="rmusr-pill is-warn">No parent email</span>;
          return (
            <button type="button" className="rmusr-btn rmusr-btn--sm" onClick={() => sendBirthday(u)} disabled={sendingBday === u.IdUser}>
              <FontAwesomeIcon icon={faCakeCandles} /> {sendingBday === u.IdUser ? 'Sending…' : 'Send email'}
            </button>
          );
        },
      },
    ],
    studentsunpaid: [
      { label: 'Student', sort: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
      { label: 'Mobile', m: 'sub', cell: phoneCell },
      { label: 'Amount due', m: 'side', sort: 'UnpaidAmount', cell: (u) => <strong className="rmusr-due">${money2(u.UnpaidAmount)}</strong> },
      { label: 'Sessions', m: 'sub', sort: 'UnpaidSessions', cell: (u) => <strong>{sessionsOf(u.UnpaidSessions)}</strong> },
      {
        label: 'Send on WhatsApp',
        action: true,
        cell: (u) => {
          const has = !!digits(u.MobileNumber);
          const btn = (kind, icon, label, cls) =>
            has ? (
              <a className={`rmusr-btn rmusr-btn--sm ${cls}`} href={waUrl(u.MobileNumber, paymentMessage(kind, u))} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={icon} /> {label}
              </a>
            ) : (
              <button type="button" className={`rmusr-btn rmusr-btn--sm ${cls}`} disabled title="No mobile number for this student">
                <FontAwesomeIcon icon={icon} /> {label}
              </button>
            );
          return (
            <span className="rmusr-wabtns">
              {btn('details', faReceipt, 'Details', 'rmusr-btn--ghost')}
              {btn('gentle', faBell, 'Reminder', 'rmusr-btn--ghost')}
              {btn('urgent', faTriangleExclamation, 'Urgent', 'rmusr-btn--warn')}
            </span>
          );
        },
      },
    ],
    demorequest: [
      {
        label: 'Student',
        main: true,
        cell: (u) => {
          const fromTz = countryFromTz(u.Timezone);
          const code = fromTz || u.CountryCode || phoneCountry(u.MobileNumber);
          const flag = flagOf(code);
          return (
            <strong className="rmusr-plain rmusr-flagname">
              {flag && (
                <span
                  className={`fi fi-${flag} rmusr-cflag`}
                  role="img"
                  aria-label={u.Timezone || u.Country || code}
                  title={fromTz ? u.Timezone : u.CountryCode ? u.Country : `${code} (from the phone number)`}
                />
              )}
              {fullName(u)}
              {u.ReferredBy && (
                <span className="rmusr-pill is-warn rmusr-referred" title={`Invited by ${u.ReferredBy} (referral link)`}>
                  <i />
                  Invited by {u.ReferredBy}
                </span>
              )}
            </strong>
          );
        },
      },
      { label: 'Age', m: 'sub', cell: (u) => (u.Age == null ? '—' : u.Age >= 19 ? 'Over 18' : u.Age) },
      {
        label: 'Course',
        m: 'sub',
        // Admin can fix the course if the parent picked the wrong one
        cell: (u) => (
          <select
            className={`rmusr-course-select ${u.InterestedCourse ? '' : 'is-empty'}`}
            value={u.InterestedCourse || ''}
            disabled={savingCourse === u.Id}
            aria-label={`Course for ${fullName(u)}`}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => changeCourse(u, e.target.value)}
          >
            <option value="">Not sure</option>
            {u.InterestedCourse && !DEMO_COURSES.some((c) => c.title === u.InterestedCourse) && (
              <option value={u.InterestedCourse}>{u.InterestedCourse}</option>
            )}
            {DEMO_COURSES.map((c) => (
              <option key={c.title} value={c.title}>
                {c.title}
              </option>
            ))}
          </select>
        ),
      },
      {
        label: 'Location',
        m: 'sub',
        cell: (u) =>
          placeOf(u) ? (
            <span className="rmusr-place">
              {placeOf(u)}
            </span>
          ) : (
            <span className="rmusr-dim">—</span>
          ),
      },
      { label: 'Parent email', m: 'hide', cell: (u) => (u.ParentsEmail ? <a href={`mailto:${u.ParentsEmail}`}>{u.ParentsEmail}</a> : <span className="rmusr-dim">—</span>) },
      { label: 'Mobile', m: 'sub', cell: phoneCell },
      { label: 'Class', m: 'sub', mdate: true, cell: (u) => <When value={u.ClassDateTime} never="—" /> },
      {
        label: 'Status', m: 'side',
        cell: (u) => <span className={`rmusr-pill ${u.approved === 1 ? 'is-good' : 'is-warn'}`}>{u.approved === 1 ? 'Approved' : 'Pending'}</span>,
      },
      {
        label: '',
        action: true,
        cell: (u) =>
          u.approved === 0 ? (
            <button type="button" className="rmusr-btn rmusr-btn--sm" onClick={() => openApprove(u)}>
              <FontAwesomeIcon icon={faCheck} /> Approve
            </button>
          ) : null,
      },
    ],
    onlineusers: [
      { label: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
      { label: 'Last sign-in', m: 'sub', cell: (u) => <When value={u.Lastsignin} never="Never signed in" /> },
      { label: 'Last activity', m: 'sub', cell: (u) => <When value={u.LastSeen} /> },
      { label: 'Status', m: 'side', cell: (u) => <Online on={u.IsOnline} /> },
    ],
    studentsupdate:
      sub === 'notscheduled'
        ? [
            { label: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
            {
              label: 'Not scheduled',
              m: 'side',
              sort: 'NotScheduled',
              cell: (u) => (
                <span className="rmusr-notsched">
                  <strong>{u.NotScheduled}</strong> {u.NotScheduled === 1 ? 'session' : 'sessions'}
                  {u.TypesReady && (
                    <small>
                      {[u.StandardLeft > 0 && `${u.StandardLeft} Standard`, u.MasteryLeft > 0 && `${u.MasteryLeft} Mastery`].filter(Boolean).join(' · ')}
                    </small>
                  )}
                </span>
              ),
            },
            {
              label: 'Next class',
              m: 'sub',
              cell: (u) =>
                u.NextClassText ? (
                  <span>
                    {u.NextClassText}
                    <span className="rmusr-dim"> · {u.ClassesLeft} booked</span>
                  </span>
                ) : (
                  <span className="rmusr-pill is-warn">
                    <i />
                    None booked
                  </span>
                ),
            },
            { label: 'Weekly slots', m: 'sub', cell: (u) => u.Slots || <span className="rmusr-dim">—</span> },
            { label: 'Last class', m: 'sub', cell: (u) => u.LastClassText || <span className="rmusr-dim">—</span> },
            {
              label: 'Mobile',
              m: 'sub',
              cell: (u) =>
                digits(u.MobileNumber) ? (
                  <a
                    className="rmusr-wa"
                    href={waUrl(
                      u.MobileNumber,
                      `Hi! ${u.FirstName || 'Your child'} still has ${u.NotScheduled} ${u.NotScheduled === 1 ? 'session' : 'sessions'} to book with RoboMind. When would you like to schedule ${u.NotScheduled === 1 ? 'it' : 'them'}? 😊`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} /> {u.MobileNumber}
                  </a>
                ) : (
                  <span className="rmusr-dim">No number</span>
                ),
            },
          ]
        : sub === 'pending'
        ? [
            { label: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
            {
              label: 'Reserved until',
              m: 'side',
              cell: (u) =>
                u.PauseUntil ? (
                  <span className={`rmusr-pill ${u.PauseOver ? 'is-bad' : 'is-warn'}`}>
                    <i />
                    {u.PauseOver ? `Ends ${u.PauseUntilText}` : u.PauseUntilText}
                  </span>
                ) : (
                  <span className="rmusr-pill is-off">
                    <i />
                    No date
                  </span>
                ),
            },
            { label: 'Weekly slots', m: 'sub', cell: (u) => (u.Slots ? <span>Reserved · {u.Slots}</span> : <span className="rmusr-dim">No slots yet</span>) },
            { label: 'Mobile', m: 'sub', cell: phoneCell },
            { label: 'Note', m: 'sub', cell: (u) => (u.SubscriptionNote ? u.SubscriptionNote : <span className="rmusr-dim">—</span>) },
          ]
        : sub === 'paused'
        ? [
            { label: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
            {
              label: 'Back on',
              m: 'side',
              cell: (u) =>
                u.PauseUntil ? (
                  <span className={`rmusr-pill ${u.PauseOver ? 'is-bad' : 'is-warn'}`}>
                    <i />
                    {u.PauseOver ? `Due ${u.PauseUntilText}` : u.PauseUntilText}
                  </span>
                ) : (
                  <span className="rmusr-pill is-off">
                    <i />
                    No date
                  </span>
                ),
            },
            {
              label: 'Weekly slots',
              m: 'sub',
              cell: (u) =>
                u.Slots ? (
                  <span>Reserved · {u.Slots}</span>
                ) : u.SavedSlotsText ? (
                  <span className="rmusr-dim">Freed · was {u.SavedSlotsText}</span>
                ) : (
                  <span className="rmusr-dim">None</span>
                ),
            },
            { label: 'Mobile', m: 'sub', cell: phoneCell },
            { label: 'Note', m: 'sub', cell: (u) => (u.SubscriptionNote ? u.SubscriptionNote : <span className="rmusr-dim">—</span>) },
            { label: 'Last class', m: 'sub', cell: (u) => u.LastClassText || <span className="rmusr-dim">—</span> },
          ]
        : [
            { label: 'Name', main: true, cell: (u) => nameCell(u, 'student') },
            { label: 'Weekly slots', m: 'sub', cell: (u) => u.Slots || <span className="rmusr-dim">—</span> },
            { label: 'Mobile', m: 'sub', cell: phoneCell },
            { label: 'Last class', m: 'sub', cell: (u) => u.LastClassText || <span className="rmusr-dim">—</span> },
            { label: 'Last activity', m: 'sub', cell: (u) => <When value={u.LastSeen} /> },
            { label: 'Status', m: 'side', cell: (u) => <Online on={u.IsOnline} /> },
          ],
  };

  /* ---------- Summary line ---------- */
  const noun = NOUN[tab] || ['user', 'users'];
  const onlineCount = rows.filter((u) => u.IsOnline).length;
  const summary = (() => {
    if (tab === 'demoavailability') {
      const teachers = new Set(rows.map((s) => s.TeacherId || s.IdUser)).size;
      return `${rows.length} slots · ${allDates.length} days · ${teachers} teachers`;
    }
    const parts = [`${rows.length} ${rows.length === 1 ? noun[0] : noun[1]}`];
    if (tab === 'studentsunpaid') {
      const due = rows.reduce((s, u) => s + (parseFloat(u.UnpaidAmount) || 0), 0);
      parts.push(`$${due.toFixed(2)} due`);
    }
    if (tab === 'demorequest') parts.push(`${rows.filter((u) => u.approved === 0).length} pending`);
    if (tab === 'studentsupdate' && sub === 'notscheduled') {
      const total = rows.reduce((n, u) => n + (u.NotScheduled || 0), 0);
      parts.push(`${total} ${total === 1 ? 'session' : 'sessions'} to book`);
      const none = rows.filter((u) => !u.NextClassText).length;
      if (none) parts.push(`${none} with no class booked`);
    }
    if (tab === 'studentsupdate' && sub === 'paused') {
      const over = rows.filter((u) => u.PauseOver).length;
      if (over) parts.push(`${over} past their return date`);
    }
    if (tab === 'birthdays') {
      const week = rows.filter((u) => u.DaysToBirthday <= 7).length;
      const today = rows.filter((u) => u.DaysToBirthday === 0).length;
      if (today) parts.push(`${today} today`);
      if (bdayDue) parts.push(`${bdayDue} ${bdayDue === 1 ? 'email' : 'emails'} to send`);
      parts.push(`${week} this week`);
    }
    if (tab === 'teachers') parts.push(`${onlineCount} online now`);
    if (hasLevel && level !== 'all') parts.push(`${levelRows.length} ${level === 'little' ? 'Little' : 'Junior'}`);
    if (tab === 'students') {
      const wrong = rows.filter((u) => ageInfo(u).mismatch).length;
      if (wrong) parts.push(`${wrong} ${wrong === 1 ? 'age' : 'ages'} to fix`);
    }
    if (tab === 'onlineusers') parts.push('refreshes every 30s');
    return parts.join(' · ');
  })();

  // "Fix" on a row with a wrong age: Age = age from that student's birthday (one student at a time)
  const fixAge = async (u) => {
    const { calc } = ageInfo(u);
    if (!window.confirm(`Change ${fullName(u)}'s age from ${u.Age ?? '—'} to ${calc}?\nBirthday: ${new Date(u.Birthday).toLocaleDateString()}`)) return;
    setFixingAge(u.IdUser);
    try {
      await axios.post(`${API}/api/admin/students/fix-ages`, { id: u.IdUser }, auth());
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.IdUser === u.IdUser ? { ...r, Age: calc } : r)) }));
      setToast(`${fullName(u)}'s age is now ${calc}.`);
    } catch (err) {
      setToast(err.response?.data?.error || 'Couldn’t update the age. Please try again.');
    } finally {
      setFixingAge(null);
    }
  };

  // Birthdays tab: "Send" the birthday email to the parents (one of 3 messages, once per birthday)
  const sendBirthday = async (u) => {
    if (!window.confirm(`Send the birthday email to ${fullName(u)}'s parents?\n${u.ParentsEmail}`)) return;
    setSendingBday(u.IdUser);
    try {
      await axios.post(`${API}/api/admin/students/birthdays/send`, { id: u.IdUser }, auth());
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.IdUser === u.IdUser ? { ...r, EmailSent: true } : r)) }));
      setBdayDue((n) => Math.max(0, n - 1));
      setToast(`Birthday email sent to ${fullName(u)}'s parents 🎉`);
    } catch (err) {
      setToast(err.response?.data?.error || 'Couldn’t send the email. Please try again.');
      if (err.response?.status === 409) setData((d) => ({ ...d, rows: d.rows.map((r) => (r.IdUser === u.IdUser ? { ...r, EmailSent: true } : r)) }));
    } finally {
      setSendingBday(null);
    }
  };

  /* ---------- Render ---------- */
  if (!userData) {
    return (
      <div className="rmusr" aria-busy="true">
        <div className="rmusr-inner">
          <div className="rmusr-skel rmusr-skel--title" />
          <div className="rmusr-skel" />
        </div>
      </div>
    );
  }

  const cols = COLUMNS[tab];

  return (
    <div className="rmusr">
      <div className="rmusr-inner">
        <header className="rmusr-head">
          <div>
            <span className="rmusr-eyebrow">People</span>
            <h1 className="rmusr-title">
              Users <span>management</span>
            </h1>
          </div>
          {tab === 'teachers' && (
            <button type="button" className="rmusr-btn" onClick={openAdd}>
              <FontAwesomeIcon icon={faPlus} /> Add teacher
            </button>
          )}
          {['students', 'studentsupdate', 'studentsunpaid'].includes(tab) && (
            <button type="button" className="rmusr-btn" onClick={() => setAddStudentOpen(true)}>
              <FontAwesomeIcon icon={faPlus} /> Add student
            </button>
          )}
        </header>

        {addStudentOpen && (
          <AddStudent
            onClose={(created) => {
              setAddStudentOpen(false);
              if (created) setReload((n) => n + 1);
            }}
          />
        )}

        <div className="rmusr-bar">
          <div className="rmusr-tabs" role="tablist" aria-label="User lists">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`rmusr-tab ${tab === t.key ? 'is-on' : ''}`}
                onClick={() => tab !== t.key && go(t.key, null)}
              >
                <FontAwesomeIcon icon={t.icon} />
                <span>{t.label}</span>
                {t.key === 'birthdays' && bdayDue > 0 && (
                  <b className="rmusr-tabbadge" aria-label={`${bdayDue} birthday ${bdayDue === 1 ? 'email' : 'emails'} to send`}>{bdayDue}</b>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ---------- Toolbar ---------- */}
        <div className="rmusr-tools">
          {tabDef.subs && (
            <div className="rmusr-seg" role="radiogroup" aria-label="Show">
              {tabDef.subs.map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={sub === k} className={sub === k ? 'is-on' : ''} onClick={() => setSub(k)}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {hasLevel && (
            <div className="rmusr-seg" role="radiogroup" aria-label="Level">
              {[
                ['all', 'All', rows.length],
                ['little', 'Little', rows.filter(isLittle).length],
                ['junior', 'Junior', rows.filter((u) => !isLittle(u)).length],
              ].map(([k, label, n]) => (
                <button key={k} type="button" role="radio" aria-checked={level === k} className={level === k ? 'is-on' : ''} onClick={() => setLevel(k)}>
                  {label} <span className="rmusr-segn">{n}</span>
                </button>
              ))}
            </div>
          )}
          {tab !== 'demoavailability' && (
            <label className="rmusr-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, parent email or ID"
                aria-label="Search"
              />
            </label>
          )}
          {cols && cols.some((c) => c.sort) && (
            <label className="rmusr-sortpick">
              <FontAwesomeIcon icon={faSort} />
              <select
                aria-label="Sort by"
                value={sortConfig.key ? `${sortConfig.key}:${sortConfig.direction}` : ''}
                onChange={(e) => {
                  const [key, direction] = e.target.value.split(':');
                  setSortConfig(key ? { key, direction } : { key: null, direction: 'asc' });
                }}
              >
                <option value="">Sort: default</option>
                {cols
                  .filter((c) => c.sort)
                  .flatMap((c) => [
                    <option key={`${c.sort}a`} value={`${c.sort}:asc`}>
                      {c.label} ↑
                    </option>,
                    <option key={`${c.sort}d`} value={`${c.sort}:desc`}>
                      {c.label} ↓
                    </option>,
                  ])}
              </select>
            </label>
          )}
          {ready && !error && (
            <p className="rmusr-summary">
              <FontAwesomeIcon icon={tab === 'onlineusers' ? faSignal : faUsers} /> {summary}
            </p>
          )}
        </div>

        {/* ---------- Content ---------- */}
        {error ? (
          <div className="rmusr-empty">
            <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
            <p>{error}</p>
            <button type="button" className="rmusr-btn" onClick={() => setReload((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : !ready ? (
          <div aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rmusr-skel" />
            ))}
          </div>
        ) : tab === 'demoavailability' ? (
          allDates.length === 0 ? (
            <div className="rmusr-empty">
              <FontAwesomeIcon icon={faCalendarDays} />
              <p>No demo availability yet. Teachers need to set their availability first.</p>
            </div>
          ) : (
            <section className="rmusr-card">
              <DateStrip dates={allDates} value={currentDay?.date} onChange={setDayKey} />
              {currentDay && (
                <ul className="rmusr-slots">
                  {Object.entries(
                    currentDay.slots.reduce((m, s) => {
                      (m[s.time] = m[s.time] || []).push(s);
                      return m;
                    }, {})
                  ).map(([time, slots]) => (
                    <li key={time}>
                      <span className="rmusr-slot-time">
                        <FontAwesomeIcon icon={faClock} /> {time}
                      </span>
                      <span className="rmusr-slot-who">
                        {slots.map((s, i) => (
                          <span key={`${s.teacherId}-${i}`} className={`rmusr-chip ${s.isTaken ? 'is-taken' : ''}`}>
                            <FontAwesomeIcon icon={faChalkboardUser} />
                            <span className="rmusr-cap">{s.teacherName || 'Teacher'}</span>
                            {s.isTaken && <em>Taken</em>}
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )
        ) : sorted.length === 0 ? (
          <div className="rmusr-empty">
            <FontAwesomeIcon icon={search ? faMagnifyingGlass : faCircleCheck} />
            <p>
              {search
                ? `No ${noun[1]} match “${search}”.`
                : tab === 'studentsupdate'
                ? sub === 'paused'
                  ? 'No paused students.'
                  : sub === 'pending'
                  ? 'No pending students (reserved, not paid).'
                  : sub === 'notscheduled'
                  ? 'Every paid session is booked.'
                  : 'Every active student has classes scheduled.'
                : `No ${tabDef.subs ? `${sub} ` : ''}${noun[1]} right now.`}
            </p>
          </div>
        ) : (
          <div className="rmusr-card rmusr-card--table">
            <table className="rmusr-table">
              <thead>
                <tr>
                  {cols.map((c, i) => (
                    <th
                      key={i}
                      scope="col"
                      className={c.action ? 'is-action' : ''}
                      aria-sort={c.sort && sortConfig.key === c.sort ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {c.sort ? (
                        <button type="button" className={sortConfig.key === c.sort ? 'is-on' : ''} onClick={() => handleSort(c.sort)}>
                          {c.label}
                          <FontAwesomeIcon icon={sortConfig.key !== c.sort ? faSort : sortConfig.direction === 'asc' ? faArrowUp : faArrowDown} />
                        </button>
                      ) : (
                        c.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((u, r) => (
                  <tr key={`${u.IdUser ?? ''}-${u.Id ?? ''}-${r}`}>
                    {cols.map((c, i) => (
                      <td
                        key={i}
                        data-label={c.label}
                        className={`${c.main ? 'is-main' : c.action ? 'is-action' : ''} ${c.m ? `is-m-${c.m}` : ''} ${c.mdate ? 'is-m-date' : ''}`.trim()}
                      >
                        {c.cell(u)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Approve demo ---------- */}
      {approve && (
        <div className="rmusr-overlay" onClick={() => !approve.submitting && setApprove(null)}>
          <div className="rmusr-modal" role="dialog" aria-modal="true" aria-label="Approve demo request" onClick={(e) => e.stopPropagation()}>
            <header className="rmusr-modal-head">
              <h2>Approve demo</h2>
              <button type="button" className="rmusr-x" onClick={() => setApprove(null)} disabled={approve.submitting} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmusr-modal-body">
              <dl className="rmusr-dl">
                <div>
                  <dt>Student</dt>
                  <dd>{fullName(approve.request)}</dd>
                </div>
                <div>
                  <dt>Age</dt>
                  <dd>{approve.request.Age ?? '—'}</dd>
                </div>
                <div>
                  <dt>Class</dt>
                  <dd>{new Date(approve.request.ClassDateTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</dd>
                </div>
                <div>
                  <dt>Time zone</dt>
                  <dd>{approve.request.Timezone || '—'}</dd>
                </div>
                <div className="is-wide">
                  <dt>Parent email</dt>
                  <dd>{approve.request.ParentsEmail || '—'}</dd>
                </div>
                <div className="is-wide">
                  <dt>Mobile</dt>
                  <dd>{approve.request.MobileNumber || '—'}</dd>
                </div>
              </dl>

              <h3 className="rmusr-h3">Available teachers</h3>
              {approve.loading ? (
                <div className="rmusr-skel rmusr-skel--small" />
              ) : approve.teachers.length === 0 ? (
                <p className="rmusr-dim">{approve.error && !approve.teachers.length ? approve.error : 'No teachers available for this time slot.'}</p>
              ) : (
                <div className="rmusr-pick" role="radiogroup" aria-label="Teacher">
                  {approve.teachers.map((t) => (
                    <label key={t.IdUser} className={approve.selected === t.IdUser ? 'is-on' : ''}>
                      <input
                        type="radio"
                        name="rmusr-teacher"
                        value={t.IdUser}
                        checked={approve.selected === t.IdUser}
                        onChange={() => setApprove((a) => ({ ...a, selected: t.IdUser }))}
                      />
                      <FontAwesomeIcon icon={faChalkboardUser} />
                      <span className="rmusr-cap">{fullName(t)}</span>
                      <small>{t.sessionsTaken ?? 0} sessions taken</small>
                    </label>
                  ))}
                </div>
              )}
              {approve.error && approve.teachers.length > 0 && <p className="rmusr-formerror">{approve.error}</p>}
            </div>
            <footer className="rmusr-modal-foot">
              <button type="button" className="rmusr-btn rmusr-btn--ghost" onClick={() => setApprove(null)} disabled={approve.submitting}>
                Cancel
              </button>
              <button type="button" className="rmusr-btn" onClick={submitApprove} disabled={approve.submitting || !approve.selected || approve.loading}>
                <FontAwesomeIcon icon={faCheck} /> {approve.submitting ? 'Approving…' : 'Approve'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ---------- One teacher's availability ---------- */}
      {avail && (
        <div className="rmusr-overlay" onClick={() => setAvail(null)}>
          <div className="rmusr-modal rmusr-modal--wide" role="dialog" aria-modal="true" aria-label="Demo availability" onClick={(e) => e.stopPropagation()}>
            <header className="rmusr-modal-head">
              <h2>
                Demo availability · <span className="rmusr-cap">{fullName(avail.teacher)}</span>
              </h2>
              <button type="button" className="rmusr-x" onClick={() => setAvail(null)} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmusr-modal-body">
              {avail.loading ? (
                <div className="rmusr-skel rmusr-skel--small" />
              ) : avail.error ? (
                <p className="rmusr-formerror">{avail.error}</p>
              ) : avail.demosOff ? (
                <p className="rmusr-dim">
                  <strong>No demo availability.</strong> This teacher turned off new demo classes, so their times are not shown in the free trial form.
                </p>
              ) : avail.dates.length === 0 ? (
                <p className="rmusr-dim">No demo availability for this teacher.</p>
              ) : (
                <>
                  <DateStrip dates={avail.dates} value={avail.day} onChange={(d) => setAvail((a) => ({ ...a, day: d }))} />
                  <div className="rmusr-times">
                    {(avail.dates.find((d) => d.date === avail.day)?.slots || []).map((s, i) => (
                      <span key={i} className={`rmusr-time ${s.isTaken ? 'is-taken' : s.isAvailable ? 'is-good' : 'is-off'}`}>
                        <strong>{s.time}</strong>
                        <small>{s.isTaken ? 'Taken' : s.isAvailable ? 'Available' : 'Unavailable'}</small>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
            <footer className="rmusr-modal-foot">
              <button type="button" className="rmusr-btn rmusr-btn--ghost" onClick={() => setAvail(null)}>
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ---------- Add teacher ---------- */}
      {addOpen && (
        <div className="rmusr-overlay" onClick={() => !adding && setAddOpen(false)}>
          <form className="rmusr-modal rmusr-modal--wide" role="dialog" aria-modal="true" aria-label="Add teacher" onClick={(e) => e.stopPropagation()} onSubmit={submitAdd}>
            <header className="rmusr-modal-head">
              <h2>Add teacher</h2>
              <button type="button" className="rmusr-x" onClick={() => setAddOpen(false)} disabled={adding} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmusr-modal-body">
              <div className="rmusr-form">
                {[
                  ['firstName', 'First name', 'text', 'off'],
                  ['lastName', 'Last name', 'text', 'off'],
                  ['email', 'Email', 'email', 'off'],
                  ['mobileNumber', 'Mobile number', 'tel', 'off'],
                  ['username', 'Username', 'text', 'off'],
                  ['birthday', 'Birthday', 'date', 'off'],
                  ['password', 'Password', 'password', 'new-password'],
                  ['confirmPassword', 'Confirm password', 'password', 'new-password'],
                ].map(([name, label, type, ac]) => (
                  <label key={name} className={`rmusr-field ${addErrors[name] ? 'is-error' : ''}`}>
                    <span>{label}</span>
                    <input
                      type={type}
                      name={name}
                      value={newTeacher[name]}
                      onChange={onNewTeacher}
                      disabled={adding}
                      autoComplete={ac}
                      aria-invalid={!!addErrors[name]}
                    />
                    {addErrors[name] && <small>{addErrors[name]}</small>}
                  </label>
                ))}
              </div>
              {addErrors.submit && <p className="rmusr-formerror">{addErrors.submit}</p>}
            </div>
            <footer className="rmusr-modal-foot">
              <button type="button" className="rmusr-btn rmusr-btn--ghost" onClick={() => setAddOpen(false)} disabled={adding}>
                Cancel
              </button>
              <button type="submit" className="rmusr-btn" disabled={adding}>
                <FontAwesomeIcon icon={faPlus} /> {adding ? 'Creating…' : 'Create teacher'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {toast && (
        <div className="rmusr-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default Users;